import { z } from 'zod';
import { getRequest, postRequest } from './amplify/helpers';
import { MediaAssetType } from '@/types/media';
import { RangeResponse } from './paginationTypes';
import { MediaAssetProviders } from '@/features/ai-video-creator/stores/productMediaSlice';
import { appView } from '@/utils/entityViews/app';

export type SocialMediaAsset = {
  id: string;
  type: MediaAssetType;
  src: string;
  previewImage?: string;
  source?: string;
  name?: string;
  createdAt?: string;
};

export enum SocialMediaSources {
  instagram = MediaAssetProviders.instagram,
  tiktok = MediaAssetProviders.tiktok,
}

export enum MediaType {
  image = 'image',
  video = 'video',
  carousel = 'carousel',
  reels = 'reels',
  story = 'story',
}

export enum ContainerStatus {
  FINISHED = 'FINISHED',
  ERROR = 'ERROR',
  EXPIRED = 'EXPIRED',
  PUBLISHED = 'PUBLISHED',
  IN_PROGRESS = 'IN_PROGRESS',
}

export enum InstagramAppScopes {
  INSTAGRAM_BASIC = 'instagram_basic',
  INSTAGRAM_SHOPPING_TAG_PRODUCTS = 'instagram_shopping_tag_products',
  INSTAGRAM_MANAGE_COMMENTS = 'instagram_manage_comments',
  INSTAGRAM_CONTENT_PUBLISH = 'instagram_content_publish',
  PAGES_SHOW_LIST = 'pages_show_list',
  PAGES_READ_ENGAGEMENT = 'pages_read_engagement',
  PAGES_MANAGE_METADATA = 'pages_manage_metadata',
  BUSINESS_MANAGEMENT = 'business_management',
}

export type SocialMediaResponse = RangeResponse<SocialMediaAsset>;
export type SocialMediaCreateSingleItemResponse = {
  id: string;
};
export type SocialMediaCreateCarouselItemResponse = SocialMediaCreateSingleItemResponse;
export type SocialMediaCreateCarouselContainerResponse = SocialMediaCreateSingleItemResponse;
export type SocialMediaPublishCarouselContainerResponse = SocialMediaCreateSingleItemResponse;
export type SocialMediaGetContainerStatusResponse = {
  status_code: string;
};
export type SocialMediaGetLimitUsageResponse = {
  data: [
    {
      quota_usage: number;
      config: {
        quota_total: number;
        quota_duration: number;
      };
    },
  ];
};

export const SocialMediaRequestSchema = z.object({
  appKey: z.string().min(1),
  source: z.nativeEnum(SocialMediaSources),
});

export const GetSocialMediaAssetsSchema = SocialMediaRequestSchema.extend({
  type: z.enum([MediaType.image, MediaType.video]).optional(),
  from: z.number().int().nonnegative(),
  size: z.number().int().positive(),
});

export const CreateSocialMediaSingleItemSchema = SocialMediaRequestSchema.extend({
  assetUrl: z.string().url(),
  mediaType: z.nativeEnum(MediaType).default(MediaType.image),
  isVideoStory: z.boolean().optional(),
  caption: z.string().optional(),
  hashtags: z.string().optional(),
  location: z.string().optional(),
});

export const CreateSocialMediaCarouselContainerSchema = CreateSocialMediaSingleItemSchema.extend({
  children: z.string(),
  mediaType: z.literal(MediaType.carousel),
}).omit({
  assetUrl: true,
  mediaType: true,
  isVideoStory: true,
});

export const PublishSocialMediaContainerSchema = SocialMediaRequestSchema.extend({
  creationId: z.string(),
});

export const GetContainerStatusSchema = SocialMediaRequestSchema.extend({
  containerId: z.string(),
});

export type GetSocialMediaAssetsRequest = z.infer<typeof GetSocialMediaAssetsSchema>;
export type CreateSocialMediaSingleItemRequest = z.infer<typeof CreateSocialMediaSingleItemSchema>;
export type CreateSocialMediaCarouselContainerRequest = z.infer<typeof CreateSocialMediaCarouselContainerSchema>;
export type PublishSocialMediaContainerRequest = z.infer<typeof PublishSocialMediaContainerSchema>;
export type GetContainerStatusRequest = z.infer<typeof GetContainerStatusSchema>;
export type GetLimitUsageRequest = z.infer<typeof SocialMediaRequestSchema>;
/**
 * This function is a defeated attempt to migrate TikTok videos query to the CDK.
 * So that's why it's directly calling Tolstoy's API.
 */
const getTiktokVideos = async (request: GetSocialMediaAssetsRequest, appId?: string): Promise<SocialMediaResponse> => {
  if (!appId) {
    return {
      items: [],
      from: 0,
      total: 0,
      itemsCount: 0,
    };
  }

  const searchParams = new URLSearchParams({
    appId,
    maxNumberOfVideos: request.size.toString(),
  });

  const url = `/tiktok/get-videos?${searchParams.toString()}`;
  console.log('url', url);
  const response = await getRequest({
    apiName: 'tiktok',
    path: url,
  });
  const data = response.data;

  console.log('data.videos', data.videos);

  const items = data.videos.map((tiktokVideo, i) => {
    const defaultName = `TikTok video #${i + 1}`;

    return {
      id: tiktokVideo.id,
      type: MediaAssetType.video,
      src: tiktokVideo.videoUrl,
      previewImage: tiktokVideo.posterUrl,
      source: MediaAssetProviders.tiktok,
      name: defaultName,
      createdAt: tiktokVideo.externalCreatedAt,
    };
  });

  return {
    items: items,
    from: request.from,
    total: items.length,
    itemsCount: items.length,
  };
};

export const getSocialMediaAssets = async (
  request: GetSocialMediaAssetsRequest,
  apps: appView[],
  signal?: AbortSignal
): Promise<SocialMediaResponse> => {
  if (request.source === SocialMediaSources.tiktok) {
    const appId = apps.find((app) => app.app === 'tiktok')?.id;
    return await getTiktokVideos(request, appId);
  }

  return postRequest({
    apiName: 'studio',
    path: '/socials-media/list',
    options: {
      body: request,
      signal,
    },
  });
};

export const createSocialMediaSingleItem = async (
  request: CreateSocialMediaSingleItemRequest,
  signal?: AbortSignal
): Promise<SocialMediaCreateSingleItemResponse> => {
  return postRequest({
    apiName: 'studio',
    path: '/socials-media/create-single-item',
    options: {
      body: request,
      signal,
    },
  });
};

export const createSocialMediaCarouselContainer = async (
  request: CreateSocialMediaCarouselContainerRequest,
  signal?: AbortSignal
): Promise<SocialMediaCreateCarouselContainerResponse> => {
  return postRequest({
    apiName: 'studio',
    path: '/socials-media/create-carousel-container',
    options: {
      body: request,
      signal,
    },
  });
};

export const publishSocialMediaContainer = async (
  request: PublishSocialMediaContainerRequest,
  signal?: AbortSignal
): Promise<SocialMediaPublishCarouselContainerResponse> => {
  return postRequest({
    apiName: 'studio',
    path: '/socials-media/publish-container',
    options: {
      body: request,
      signal,
    },
  });
};

export const getContainerStatus = async (
  request: GetContainerStatusRequest,
  signal?: AbortSignal
): Promise<SocialMediaGetContainerStatusResponse> => {
  return getRequest({
    apiName: 'studio',
    path: '/socials-media/get-container-status',
    options: {
      queryParams: request,
      signal,
    },
  });
};

export const getLimitUsage = async (
  request: GetLimitUsageRequest,
  signal?: AbortSignal
): Promise<SocialMediaGetLimitUsageResponse> => {
  return getRequest({
    apiName: 'studio',
    path: '/socials-media/get-limit-usage',
    options: {
      queryParams: request,
      signal,
    },
  });
};
